// Field Data Library — original wording only. Not copied from any handbook.

export const CONSTANTS = [
  { label: "1 barrel (US oil)", value: "42 US gal = 5.615 ft³" },
  { label: "1 ft³", value: "≈ 0.1781 bbl" },
  { label: "1 ft³ to gallons", value: "7.48052 US gal" },
  { label: "1 m³ to barrels", value: "≈ 6.28981 bbl" },
  { label: "Hydrostatic constant (ppg, ft)", value: "0.052" },
  { label: "Freshwater Gradient", value: "≈ 0.433 psi/ft" },
  { label: "Saltwater Gradient (avg)", value: "≈ 0.465 psi/ft" },
  { label: "Capacity Constant (in², bbl/ft)", value: "1029.4" },
  { label: "Steel Density (avg)", value: "65.5 ppg (≈ 489 lb/ft³)" },
  { label: "Barite Density", value: "≈ 35.0 ppg / 4.20 SG" },
  { label: "Freshwater Density", value: "8.33 ppg / 1.00 SG" },
  { label: "SG → ppg", value: "1 SG = 8.345 ppg" },
  { label: "ppg → kg/m³", value: "1 ppg ≈ 119.826 kg/m³" },
  { label: "psi → bar", value: "1 psi ≈ 0.06895 bar" },
  { label: "ft → m", value: "1 ft = 0.3048 m" },
];

export const NOTES = [
  {
    title: "Pressure Gradient — what it really tells you",
    body: "Pressure gradient describes how quickly fluid pressure increases with depth. " +
      "Multiplying mud weight (in ppg) by 0.052 gives you the gradient in psi per foot of vertical depth. " +
      "Use TVD, not measured depth, when working out hydrostatic pressure on a deviated well.",
  },
  {
    title: "Hydrostatic Pressure — practical view",
    body: "Hydrostatic pressure is the static pressure created by the vertical fluid column. " +
      "It does not depend on hole geometry or volume — only on density and true vertical depth. " +
      "In a kick scenario, hydrostatic plus surface shut-in pressure must equal or exceed formation pressure to maintain control.",
  },
  {
    title: "Unit Conversion — quick rules",
    body: "1 psi ≈ 6.895 kPa. 1 bar = 100 kPa ≈ 14.504 psi. 1 bbl = 42 US gal = 5.615 ft³ ≈ 158.99 L. " +
      "1 ppg ≈ 119.83 kg/m³ ≈ 0.1198 SG (relative to fresh water). 1 m = 3.2808 ft.",
  },
  {
    title: "Safety reminder",
    body: "Calculations support decisions; they do not replace them. Always verify inputs (especially TVD vs MD and unit systems), " +
      "have a second person cross-check well-control numbers, and follow company well-control procedures.",
  },
];

export const DRILLING_GLOSSARY = [
  { term: "MD", def: "Measured Depth — distance along the wellbore path." },
  { term: "TVD", def: "True Vertical Depth — straight-line vertical distance from a depth datum." },
  { term: "ROP", def: "Rate of Penetration — drilled depth per unit of time." },
  { term: "WOB", def: "Weight on Bit — axial force applied to the bit." },
  { term: "RPM", def: "Revolutions per Minute — rotary speed of the drillstring." },
  { term: "SPM", def: "Strokes per Minute — pump speed in mud-pump strokes per minute." },
  { term: "GPM", def: "Gallons per Minute — fluid flow rate, US gallons per minute." },
  { term: "BHA", def: "Bottom-Hole Assembly — collection of tools at the lower end of the drillstring." },
  { term: "BOP", def: "Blowout Preventer — wellhead pressure-control stack." },
  { term: "ECD", def: "Equivalent Circulating Density — effective density at depth while circulating." },
  { term: "FIT", def: "Formation Integrity Test — pressure test to a planned value below leak-off." },
  { term: "LOT", def: "Leak-Off Test — pressure test where the formation begins to take fluid." },
  { term: "SIDPP", def: "Shut-In Drill Pipe Pressure — surface pressure on drill pipe after shut-in." },
  { term: "SICP", def: "Shut-In Casing Pressure — surface pressure on the annulus after shut-in." },
  { term: "KMW", def: "Kill Mud Weight — mud weight required to balance formation pressure." },
  { term: "ICP", def: "Initial Circulating Pressure — pressure at the start of a kill circulation." },
  { term: "FCP", def: "Final Circulating Pressure — pressure when kill mud is at the bit." },
  { term: "SCR", def: "Slow Circulation Rate — reduced pump rate used for well-control circulation." },
  { term: "DP", def: "Drill Pipe — tubular conduit transmitting torque and circulating fluid." },
];

export const MUD_GLOSSARY = [
  { term: "MW", def: "Mud Weight — fluid density, usually in ppg." },
  { term: "PV", def: "Plastic Viscosity — Bingham model viscosity at high shear." },
  { term: "YP", def: "Yield Point — Bingham model stress that must be exceeded before flow." },
  { term: "Gel Strength", def: "Static shear stress (lb/100ft²) measured after a quiescent period." },
  { term: "Filtrate", def: "Liquid driven from the mud into a porous wall through a filter cake." },
  { term: "Filter Cake", def: "Solids deposited on the borehole wall during filtration." },
  { term: "Retort", def: "Device used to measure the oil/water/solids fractions in a mud sample." },
  { term: "Funnel Viscosity", def: "Time (s) for a quart of mud to flow through a Marsh funnel." },
  { term: "Barite", def: "Barium sulfate weighting agent; ~35 ppg / 4.20 SG." },
  { term: "Bentonite", def: "Sodium-montmorillonite clay used to build viscosity in water-based mud." },
  { term: "Brine", def: "High-density water-based fluid (NaCl, CaCl₂, CaBr₂, etc.) used for completion or kill." },
  { term: "OWR", def: "Oil-Water Ratio — percentage split between oil and water phases in invert/synthetic mud." },
];

export const CEMENT_GLOSSARY = [
  { term: "Slurry", def: "The pumpable mixture of cement, water, and additives." },
  { term: "Yield", def: "Volume of slurry produced per sack of dry cement (ft³/sx)." },
  { term: "Thickening Time", def: "Time before the slurry becomes too viscous to pump." },
  { term: "Spacer", def: "Fluid pumped between mud and cement to clean the hole and prevent contamination." },
  { term: "Lead Cement", def: "Lower-density slurry pumped first, generally to fill the upper annulus." },
  { term: "Tail Cement", def: "Higher-density slurry pumped last, sealing the bottom of the annulus." },
  { term: "Displacement", def: "Volume pumped behind cement to push it into final position." },
  { term: "Top of Cement", def: "Estimated depth of the cement column in the annulus (TOC)." },
  { term: "Float Collar", def: "Backflow-prevention device located above the float shoe." },
  { term: "Shoe Track", def: "Volume between float collar and float shoe (typically a few joints)." },
  { term: "WOC", def: "Wait on Cement — time waiting for sufficient set strength before drilling out." },
];

export const WELLCONTROL_GLOSSARY = [
  { term: "Kick", def: "Unwanted influx of formation fluid into the wellbore." },
  { term: "Shut-in", def: "Sealing the well at surface to read pressures and control influx." },
  { term: "SIDPP", def: "Shut-In Drill Pipe Pressure — drill-pipe-side surface pressure once stabilised." },
  { term: "SICP", def: "Shut-In Casing Pressure — annulus-side surface pressure once stabilised." },
  { term: "KMW", def: "Kill Mud Weight — mud weight that balances formation pressure at TVD." },
  { term: "ICP", def: "Initial Circulating Pressure — typically SIDPP + slow-rate pump pressure." },
  { term: "FCP", def: "Final Circulating Pressure — slow-rate × (KMW/OMW) when kill mud reaches bit." },
  { term: "Choke", def: "Variable orifice used to control annulus pressure during circulation." },
  { term: "Kill Line", def: "High-pressure surface line dedicated to pumping into the well during well-control operations." },
  { term: "Trip Margin", def: "Additional mud weight (or pressure equivalent) for safe tripping." },
  { term: "MAASP", def: "Maximum Allowable Annular Surface Pressure — limit before reaching shoe leak-off." },
];

export const DIRECTIONAL_GLOSSARY = [
  { term: "RKB", def: "Rotary Kelly Bushing — common depth datum at the rig floor." },
  { term: "MD", def: "Measured Depth — distance along the wellbore." },
  { term: "TVD", def: "True Vertical Depth — straight vertical from the depth datum." },
  { term: "NS", def: "Northing — north-positive horizontal displacement." },
  { term: "EW", def: "Easting — east-positive horizontal displacement." },
  { term: "DLS", def: "Dogleg Severity — change in wellbore direction per 100 ft." },
  { term: "BUR", def: "Build-Up Rate — rate of inclination increase per 100 ft." },
  { term: "TR", def: "Turn Rate — rate of azimuth change per 100 ft." },
  { term: "VS", def: "Vertical Section — projected horizontal travel along the target azimuth." },
  { term: "KOP", def: "Kick-Off Point — depth where the well begins to deviate from vertical." },
  { term: "Sail Angle", def: "Constant inclination held over a tangent section." },
  { term: "Closure", def: "Straight-line horizontal distance from surface location to bottomhole." },
];

export const STUCK_FISHING_GLOSSARY = [
  { term: "Free Point", def: "Deepest depth at which pipe is free to move under pull or torque." },
  { term: "Stuck Point", def: "Depth at or below which pipe will not move under safe loads." },
  { term: "Differential Stuck", def: "Stuck due to higher mud column pressure pressing pipe into the filter cake at a permeable formation." },
  { term: "Mechanical Stuck", def: "Stuck due to hole geometry: doglegs, ledges, key seats, undergauge hole, cuttings beds." },
  { term: "Jar", def: "Hydraulic or mechanical tool that delivers a sharp impact load to free stuck pipe." },
  { term: "Free-Point Tool", def: "Wireline tool measuring strain along pipe to locate the free point." },
  { term: "Back-Off", def: "Controlled unscrewing of a tool joint near the stuck point to recover the free pipe." },
  { term: "Washover", def: "Running a slightly larger string over the stuck pipe to remove obstructing material." },
  { term: "Overshot / Spear", def: "Fishing tools that grip the outside (overshot) or inside (spear) of the fish." },
  { term: "Pipe Light / Pipe Heavy", def: "Hookload below or above buoyed string weight — used to detect partial sticking." },
];


export const CASING_GLOSSARY = [
  { term: "OD", def: "Outside Diameter — outer cross-section diameter of a tubular." },
  { term: "ID", def: "Inside Diameter — inner bore diameter of a tubular." },
  { term: "Drift", def: "Maximum diameter that will pass through a tubular (always ≤ ID)." },
  { term: "Burst", def: "Internal pressure that a tubular can withstand before yielding outward." },
  { term: "Collapse", def: "External pressure that a tubular can withstand before crushing inward." },
  { term: "Tensile Rating", def: "Maximum axial load a tubular can carry before yielding." },
  { term: "Coupling", def: "Threaded connection between two pipe joints." },
  { term: "Shoe", def: "Guide piece at the bottom of a casing string, often with cement-float function." },
  { term: "Liner", def: "Tubular string hung off below a previous casing, not run back to surface." },
  { term: "Tubing", def: "Production conduit run inside the casing through which produced fluids flow." },
];

export const VERIFICATION_REMINDERS = [
  "Always confirm units before pressing Calculate — psi vs bar, ppg vs SG, ft vs m.",
  "Always verify TVD vs MD — every depth-driven calculation depends on TVD, not MD.",
  "Cross-check gauge readings (pressure, pit volume, pump output) against a second instrument or person.",
  "Use company-approved worksheets and procedures for any critical operation.",
  "Do not use RigSentinel output as the sole basis for well-control decisions.",
  "Snapshot your inputs and results (PDF / Save) before acting in the field.",
];

export const SAFETY_NOTE = {
  title: "Safety Reminder",
  body:
    "RigSentinel is a computational aid for trained personnel. It is not a live well-control system, not a substitute for procedures, " +
    "and not a replacement for engineering judgment. Verify every input, every unit, every formula, and every result with a qualified second person before acting on the field. " +
    "If a well-control situation is in progress, follow your company well-control program, your regulatory requirements, and instructions from certified well-control personnel.",
};

export const TRUST_NOTE =
  "Built for field calculation support. Not a replacement for engineering judgment, certified personnel, approved company procedures, or regulatory requirements.";

// Flat searchable index of every field-data term/note for the Home search bar.
export function getFieldDataIndex() {
  const out = [];
  CONSTANTS.forEach((c) => out.push({ type: "constant", title: c.label, body: c.value, target: "/library" }));
  NOTES.forEach((n) => out.push({ type: "note", title: n.title, body: n.body, target: "/library" }));
  const glossaries = [
    ["Drilling", DRILLING_GLOSSARY],
    ["Mud", MUD_GLOSSARY],
    ["Cement", CEMENT_GLOSSARY],
    ["Well-Control", WELLCONTROL_GLOSSARY],
    ["Casing & Tubing", CASING_GLOSSARY],
  ];
  glossaries.forEach(([cat, list]) => {
    list.forEach((g) => out.push({ type: `${cat} term`, title: g.term, body: g.def, target: "/library" }));
  });
  DIRECTIONAL_GLOSSARY.forEach((g) => out.push({ type: "Directional term", title: g.term, body: g.def, target: "/library" }));
  STUCK_FISHING_GLOSSARY.forEach((g) => out.push({ type: "Stuck/Fishing term", title: g.term, body: g.def, target: "/library" }));
  VERIFICATION_REMINDERS.forEach((v, i) => out.push({ type: "verification", title: `Reminder ${i + 1}`, body: v, target: "/library" }));
  return out;
}
